import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParam } from 'react-use';

import { getThemePropsLumaBot } from 'core/styles';
import { defaultsDeep, get, isNull, isString } from 'lodash';
import WebFont from 'webfontloader';

import { fileUploadsResource } from '@lib/resources';
import { Setting } from '@lib/resources/types';
import { selectCurrentMasterSettings, selectCurrentUserSettings } from '@lib/selectors';
import configService from '@lib/services/configService';
import storageService from '@lib/services/storageService';

export default function useLumaBotSettings(): Setting['lumabot'] {
  const avatar = useSearchParam('avatar');
  const header = useSearchParam('header');
  const includePoweredByLuma = useSearchParam('includePoweredByLuma');
  const name = useSearchParam('name');
  const placement = useSearchParam('placement');
  const prompt = useSearchParam('prompt');
  const theme = useSearchParam('theme');

  const settingsUser = useSelector(selectCurrentUserSettings);
  const settingsMaster = useSelector(selectCurrentMasterSettings);
  const isHideProviderDetails: boolean = get(settingsMaster, 'scheduler.hideProviderDetails', false);
  const settings: Setting | undefined = settingsUser || settingsMaster;
  const settingsLumaBot: Setting['lumabot'] | undefined = get(settings, 'lumabot');
  const [result, setResult] = useState({
    theme: getThemePropsLumaBot(isNull(theme) ? undefined : JSON.parse(theme)),
    ...settingsLumaBot,
  });
  const [avatarDataURL, setAvatarDataURL] = useState<string>();

  useEffect(() => {
    // Ensure we don't clear settings to avoid flickering
    if (!settingsLumaBot) return;

    // Extend with URL params for preview purposes
    const paramsLumaBot: Partial<Setting['lumabot']> = {
      avatar: isNull(avatar) ? undefined : avatar,
      header: isNull(header) ? undefined : header,
      name: isNull(name) ? undefined : name,
      placement: isNull(placement) ? undefined : (placement as Setting['lumabot']['placement']),
      prompt: isNull(prompt) ? undefined : prompt,
      theme: isNull(theme) ? undefined : JSON.parse(theme),
      // TODO: Remove once it's tested properly since customers would be able to pass this URL parameter
      // to the iframe to skip rendering the `PoweredByLuma` text
      includePoweredByLuma: isString(includePoweredByLuma) ? includePoweredByLuma === 'true' : undefined,
    };

    // Merge values
    const newResult = defaultsDeep({}, paramsLumaBot, settingsLumaBot);
    setResult({
      ...newResult,
      theme: getThemePropsLumaBot(newResult.theme),
    });
  }, [settingsLumaBot, avatar, header, includePoweredByLuma, name, placement, prompt, theme]);

  // Load the font in case it's missing
  const fontFamily = get(result, 'theme.typography.fontFamily');
  useEffect(() => {
    if (!fontFamily) return;
    WebFont.load({ google: { families: [fontFamily] } });
  }, [fontFamily]);

  // Load and cache the avatar as base64
  useEffect(() => {
    if (!result.avatar) {
      setAvatarDataURL('');
    } else {
      const src = `${configService.getApiBasePath()}${fileUploadsResource.url}/${
        result.avatar
      }/file?token=${storageService.getToken()}`;
      const xhr = new XMLHttpRequest();
      xhr.onload = () => {
        const reader = new FileReader();
        reader.onloadend = () => setAvatarDataURL(reader.result as string);
        reader.readAsDataURL(xhr.response);
      };
      xhr.onerror = () => setAvatarDataURL('');
      xhr.open('GET', src);
      xhr.responseType = 'blob';
      xhr.send();
    }
  }, [result.avatar]);

  return { ...result, avatar: avatarDataURL, isHideProviderDetails };
}
